import { useCallback, useState } from "react";

const useCartQuantityHandlers = ({ cartItems, updateCartQuantityFromBackend, toastRef, confirm, onRemoveItem }) => {
  const [updatingQtyLineKey, setUpdatingQtyLineKey] = useState(null);

  const applyQtyChange = useCallback(async (lineKey, nextQty) => {
    const normalizedQty = Math.max(1, Number(nextQty || 1));
    const item = cartItems.find((it) => it.lineKey === lineKey);

    if (!item?.code) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Error",
        detail: "No se pudo identificar el producto.",
        life: 3000,
      });
      return;
    }

    setUpdatingQtyLineKey(lineKey);

    try {
      const result = await updateCartQuantityFromBackend(item.code, normalizedQty);

      if (!result.success) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Problemas de Sincronización",
          detail: result.error || "La cantidad no se sincronizó con el servidor.",
          life: 3000,
        });
      }
    } catch (error) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Problemas de Sincronización",
        detail: error?.message || "La cantidad no se sincronizó con el servidor.",
        life: 3000,
      });
    } finally {
      setUpdatingQtyLineKey(null);
    }
  }, [cartItems, updateCartQuantityFromBackend, toastRef]);

  const handleDecreaseQty = useCallback(async (lineKey, quantity) => {
    const currentQty = Number(quantity || 1);
    if (currentQty <= 1) {
      const shouldRemove = await confirm("¿Deseas eliminar este producto del carro?");
      if (shouldRemove) {
        await onRemoveItem(lineKey);
      }
      return;
    }
    await applyQtyChange(lineKey, currentQty - 1);
  }, [applyQtyChange, confirm, onRemoveItem]);

  const handleIncreaseQty = useCallback(async (lineKey, quantity) => {
    await applyQtyChange(lineKey, Number(quantity || 1) + 1);
  }, [applyQtyChange]);

  const handleChangeQty = useCallback(async (lineKey, nextQty, currentQty) => {
    const parsedQty = Number(nextQty);
    const normalizedCurrentQty = Math.max(1, Number(currentQty || 1));
    if (!Number.isFinite(parsedQty) || parsedQty <= 0) {
      if (normalizedCurrentQty <= 1) {
        const shouldRemove = await confirm("¿Deseas eliminar este producto del carro?");
        if (shouldRemove) {
          await onRemoveItem(lineKey);
        }
        return;
      }
      await applyQtyChange(lineKey, 1);
      return;
    }
    if (Math.floor(parsedQty) === normalizedCurrentQty) {
      return;
    }
    await applyQtyChange(lineKey, parsedQty);
  }, [applyQtyChange, confirm, onRemoveItem]);

  const isUpdatingQty = useCallback((lineKey) => updatingQtyLineKey === lineKey, [updatingQtyLineKey]);

  return {
    updatingQtyLineKey,
    handleDecreaseQty,
    handleIncreaseQty,
    handleChangeQty,
    isUpdatingQty,
  };
};

export default useCartQuantityHandlers;
